/**
 * FeatureLockedScreen — Verkaufsstarker Sperrbildschirm für Module,
 * die ein aktives Abo (inkl. SEPA-Mandat) voraussetzen.
 *
 * Beispiele: Shop, Werbematerial — nutzbar erst, sobald der User einen
 * Plan gewählt und das SEPA-Mandat unterschrieben hat.
 */
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Lock, ArrowRight, Check, Sparkles } from "lucide-react";
import UpsellDialog from "./UpsellDialog";

export default function FeatureLockedScreen({
  icon: Icon = Lock,
  title,
  subtitle,
  bullets = [],
  trigger = "feature-gate",
}) {
  const [upsellOpen, setUpsellOpen] = useState(false);

  return (
    <div className="max-w-3xl mx-auto" data-testid="feature-locked-screen">
      <div className="rounded-3xl border border-cyan-200 bg-white overflow-hidden shadow-sm">
        <div className="brand-gradient-bg text-white px-6 py-9 sm:px-10 sm:py-12 text-center relative overflow-hidden">
          <div className="absolute -right-12 -top-12 w-56 h-56 rounded-full bg-white/10 blur-3xl pointer-events-none" />
          <div className="absolute -left-12 -bottom-12 w-64 h-64 rounded-full bg-cyan-300/20 blur-3xl pointer-events-none" />

          <div className="relative">
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-3 py-1.5 mb-5">
              <Sparkles className="h-3.5 w-3.5" />
              <span className="text-[10px] uppercase tracking-[0.28em] font-bold">Premium-Modul</span>
            </div>
            <div className="h-14 w-14 rounded-2xl bg-white/15 backdrop-blur-sm mx-auto flex items-center justify-center mb-4">
              <Icon className="h-6 w-6 text-white" strokeWidth={1.6} />
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold leading-tight max-w-xl mx-auto">
              {title}
            </h1>
            <p className="mt-2.5 text-sm sm:text-base text-white/90 max-w-xl mx-auto leading-relaxed">
              {subtitle}
            </p>
          </div>
        </div>

        {bullets.length > 0 && (
          <div className="px-6 sm:px-10 py-8">
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 max-w-2xl mx-auto">
              {bullets.map((b) => (
                <div key={b} className="flex items-start gap-2.5 text-sm">
                  <Check className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" strokeWidth={2.4} />
                  <span className="text-zinc-700 leading-snug">{b}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="px-6 sm:px-10 pb-9 text-center">
          <Button
            onClick={() => setUpsellOpen(true)}
            size="lg"
            className="brand-gradient-bg text-white font-bold shadow-md"
            data-testid="feature-locked-cta"
          >
            Jetzt freischalten — Plan wählen
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
          <div className="text-[11px] text-zinc-500 mt-3">
            SEPA-Lastschrift · 3 Monate Mindestlaufzeit · keine Kreditkarte
          </div>
        </div>
      </div>

      <UpsellDialog
        open={upsellOpen}
        onClose={() => setUpsellOpen(false)}
        headline={`${title} freischalten?`}
        subheadline="Wähle einen Plan und nutze sofort alle Premium-Module — Shop, Werbematerial, DAT und vieles mehr."
        trigger={trigger}
      />
    </div>
  );
}
